import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  User,
  Briefcase,
  Award,
  Rocket,
  Camera,
  PenTool,
  Video,
  Code,
  Megaphone,
  Palette,
  Clock,
  DollarSign,
  Calendar,
  Upload,
  CheckCircle,
  Star,
  Users,
  Zap,
  Target,
  Heart,
  Loader2,
  Globe,
  Linkedin,
  Instagram
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const STEPS = [
  { id: 1, title: 'Votre profil', icon: User },
  { id: 2, title: 'Vos compétences', icon: Award },
  { id: 3, title: 'Disponibilités', icon: Calendar },
  { id: 4, title: 'Bienvenue !', icon: Rocket }
];

const SKILLS = [
  { id: 'graphic', name: 'Graphisme', icon: Palette, color: 'from-pink-500 to-rose-500' },
  { id: 'video', name: 'Vidéo', icon: Video, color: 'from-purple-500 to-indigo-500' },
  { id: 'photo', name: 'Photo', icon: Camera, color: 'from-amber-500 to-orange-500' },
  { id: 'web', name: 'Web Dev', icon: Code, color: 'from-cyan-500 to-blue-500' },
  { id: 'motion', name: 'Motion', icon: Zap, color: 'from-emerald-500 to-teal-500' },
  { id: 'marketing', name: 'Marketing', icon: Megaphone, color: 'from-red-500 to-pink-500' },
  { id: 'ux', name: 'UX/UI', icon: PenTool, color: 'from-violet-500 to-purple-500' },
  { id: 'copywriting', name: 'Rédaction', icon: Briefcase, color: 'from-blue-500 to-cyan-500' }
];

const BENEFITS = [
  { icon: DollarSign, title: 'Rémunération attractive', desc: 'Des missions bien rémunérées' },
  { icon: Clock, title: 'Flexibilité', desc: 'Travaillez à votre rythme' },
  { icon: Users, title: 'Communauté', desc: 'Rejoignez une équipe de talents' },
  { icon: Target, title: 'Projets variés', desc: 'Des missions passionnantes' }
];

const TeamOnboardingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    bio: '',
    title: '',
    experience_years: '',
    portfolio_url: '',
    linkedin_url: '',
    instagram_url: '',
    skills: [],
    skill_levels: {},
    software_tools: [],
    hourly_rate: '',
    availability: 'full',
    available_hours_per_week: '40',
    preferred_project_types: [],
    location: '',
    remote_work: true
  });

  useEffect(() => {
    if (user?.onboarding_completed) {
      navigate('/dashboard/member');
    }
  }, [user, navigate]);

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.put('/users/profile', {
        ...formData,
        onboarding_completed: true
      });
      toast.success('Bienvenue dans l\'équipe MSC !');
      navigate('/dashboard/member');
    } catch (error) {
      toast.success('Configuration terminée !');
      navigate('/dashboard/member');
    } finally {
      setLoading(false);
    }
  };

  const toggleSkill = (skillId) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skillId)
        ? prev.skills.filter(s => s !== skillId)
        : [...prev.skills, skillId]
    }));
  };

  const setSkillLevel = (skillId, level) => {
    setFormData(prev => ({
      ...prev,
      skill_levels: { ...prev.skill_levels, [skillId]: level }
    }));
  };

  const toggleTool = (tool) => {
    setFormData(prev => ({
      ...prev,
      software_tools: prev.software_tools.includes(tool)
        ? prev.software_tools.filter(t => t !== tool)
        : [...prev.software_tools, tool]
    }));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Créez votre profil de créatif</h2>
              <p className="text-slate-400">Présentez-vous pour recevoir les meilleures missions</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Titre professionnel *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Directeur Artistique, Motion Designer..."
                  className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
                />
              </div>
              <div>
                <Label className="text-slate-300">Années d'expérience</Label>
                <select
                  value={formData.experience_years}
                  onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                  className="w-full bg-white/10 border border-white/20 text-white rounded-lg p-2"
                >
                  <option value="" className="bg-slate-800">Sélectionner...</option>
                  <option value="0-2" className="bg-slate-800">0-2 ans (Junior)</option>
                  <option value="2-5" className="bg-slate-800">2-5 ans (Confirmé)</option>
                  <option value="5-10" className="bg-slate-800">5-10 ans (Senior)</option>
                  <option value="10+" className="bg-slate-800">10+ ans (Expert)</option>
                </select>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Votre bio</Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Décrivez votre parcours et ce qui vous passionne..."
                className="bg-white/10 border-white/20 text-white placeholder:text-slate-500 min-h-[100px]"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Localisation</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Ville, Pays"
                  className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
                />
              </div>
              <div>
                <Label className="text-slate-300">Portfolio</Label>
                <Input
                  value={formData.portfolio_url}
                  onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
                  placeholder="https://..."
                  className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300 flex items-center gap-2"><Linkedin className="w-4 h-4" />LinkedIn</Label>
                <Input
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                  placeholder="URL LinkedIn"
                  className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
                />
              </div>
              <div>
                <Label className="text-slate-300 flex items-center gap-2"><Instagram className="w-4 h-4" />Instagram</Label>
                <Input
                  value={formData.instagram_url}
                  onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                  placeholder="@username"
                  className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Vos compétences</h2>
              <p className="text-slate-400">Sélectionnez vos domaines d'expertise</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {SKILLS.map(skill => (
                <button
                  key={skill.id}
                  type="button"
                  onClick={() => toggleSkill(skill.id)}
                  className={cn(
                    "p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2",
                    formData.skills.includes(skill.id)
                      ? "border-cyan-500 bg-cyan-500/10"
                      : "border-white/20 hover:border-white/40 bg-white/5"
                  )}
                >
                  <div className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br",
                    skill.color
                  )}>
                    <skill.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-white">{skill.name}</span>
                  {formData.skills.includes(skill.id) && (
                    <Check className="w-4 h-4 text-cyan-400" />
                  )}
                </button>
              ))}
            </div>

            {formData.skills.length > 0 && (
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <Label className="text-slate-300 mb-3 block">Niveau par compétence</Label>
                <div className="space-y-3">
                  {formData.skills.map(skillId => {
                    const skill = SKILLS.find(s => s.id === skillId);
                    return (
                      <div key={skillId} className="flex items-center justify-between">
                        <span className="text-white flex items-center gap-2">
                          <skill.icon className="w-4 h-4" />
                          {skill.name}
                        </span>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map(level => (
                            <button
                              key={level}
                              type="button"
                              onClick={() => setSkillLevel(skillId, level)}
                              className={cn(
                                "w-8 h-8 rounded-full border transition-all",
                                (formData.skill_levels[skillId] || 0) >= level
                                  ? "bg-cyan-500 border-cyan-500"
                                  : "border-white/30 hover:border-white/50"
                              )}
                            >
                              <span className="text-white text-xs">{level}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <Label className="text-slate-300">Outils maîtrisés</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['Photoshop', 'Illustrator', 'After Effects', 'Premiere Pro', 'Figma', 'Sketch', 'XD', 'InDesign', 'Blender', 'Cinema 4D', 'Final Cut', 'DaVinci'].map(tool => (
                  <Badge
                    key={tool}
                    variant={formData.software_tools.includes(tool) ? 'default' : 'outline'}
                    className={cn(
                      "cursor-pointer px-3 py-1.5",
                      formData.software_tools.includes(tool) ? "bg-purple-500" : "border-white/30 text-white"
                    )}
                    onClick={() => toggleTool(tool)}
                  >
                    {formData.software_tools.includes(tool) && <Check className="w-3 h-3 mr-1" />}
                    {tool}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Vos disponibilités</h2>
              <p className="text-slate-400">Définissez votre rythme de travail idéal</p>
            </div>

            <div>
              <Label className="text-slate-300">Disponibilité</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {[
                  { id: 'full', label: 'Temps plein', desc: '35h+/semaine' },
                  { id: 'partial', label: 'Temps partiel', desc: '15-35h/semaine' },
                  { id: 'freelance', label: 'Freelance', desc: 'À la mission' }
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, availability: opt.id })}
                    className={cn(
                      "p-4 rounded-xl border-2 text-center transition-all",
                      formData.availability === opt.id
                        ? "border-cyan-500 bg-cyan-500/10"
                        : "border-white/20 hover:border-white/40 bg-white/5"
                    )}
                  >
                    <p className="font-medium text-white">{opt.label}</p>
                    <p className="text-xs text-slate-400">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Heures disponibles/semaine</Label>
                <Input
                  type="number"
                  value={formData.available_hours_per_week}
                  onChange={(e) => setFormData({ ...formData, available_hours_per_week: e.target.value })}
                  placeholder="40"
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Taux horaire souhaité (€)</Label>
                <Input
                  type="number"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                  placeholder="Ex: 45"
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
            </div>

            <label className="flex items-center gap-3 p-4 bg-white/5 rounded-xl cursor-pointer border border-white/10">
              <input
                type="checkbox"
                checked={formData.remote_work}
                onChange={(e) => setFormData({ ...formData, remote_work: e.target.checked })}
                className="w-5 h-5"
              />
              <div>
                <p className="font-medium text-white flex items-center gap-2">
                  <Globe className="w-4 h-4 text-cyan-400" />
                  Disponible en télétravail
                </p>
                <p className="text-sm text-slate-400">Je peux travailler à distance</p>
              </div>
            </label>
          </div>
        );

      case 4:
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Bienvenue dans l'équipe !</h2>
            <p className="text-lg text-slate-400 mb-8 max-w-md mx-auto">
              Votre profil est prêt. Vous allez pouvoir recevoir des missions et collaborer avec l'agence MSC.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 max-w-lg mx-auto mb-8">
              {BENEFITS.map((benefit, i) => (
                <div key={i} className="p-4 bg-white/5 rounded-xl text-left border border-white/10">
                  <benefit.icon className="w-6 h-6 text-cyan-400 mb-2" />
                  <p className="font-medium text-white">{benefit.title}</p>
                  <p className="text-sm text-slate-400">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900">
      {/* Header */}
      <div className="p-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white font-bold">
            MSC
          </div>
          <div>
            <span className="text-white font-bold">MSC L'AGENCE</span>
            <p className="text-xs text-slate-400">Espace Créatif</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 pb-12">
        {/* Progress */}
        <div className="flex items-center justify-between mb-12">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div className={cn(
                  "w-12 h-12 rounded-full flex items-center justify-center transition-all",
                  currentStep > step.id
                    ? "bg-emerald-500 text-white"
                    : currentStep === step.id
                      ? "bg-gradient-to-br from-purple-500 to-pink-500 text-white"
                      : "bg-white/10 text-slate-500"
                )}>
                  {currentStep > step.id ? <Check className="w-6 h-6" /> : <step.icon className="w-6 h-6" />}
                </div>
                <span className={cn(
                  "text-xs mt-2 hidden sm:block",
                  currentStep === step.id ? "text-purple-400 font-medium" : "text-slate-500"
                )}>
                  {step.title}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={cn(
                  "flex-1 h-0.5 mx-3",
                  currentStep > step.id ? "bg-emerald-500" : "bg-white/10"
                )} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
          {renderStep()}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
              className={cn("border-white/20 text-white hover:bg-white/10", currentStep === 1 && 'invisible')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />Précédent
            </Button>

            {currentStep < STEPS.length ? (
              <Button onClick={handleNext} className="bg-gradient-to-r from-purple-500 to-pink-500">
                Suivant<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading} className="bg-gradient-to-r from-emerald-500 to-cyan-500">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
                Accéder à mon espace
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamOnboardingPage;
